'use client';

import React from 'react';
import SlideWrapper from '../SlideWrapper';
import { Utensils, TreePine, Fish, Hammer, Camera, Users } from 'lucide-react';

interface Props {
  id: string;
  registerRef: (id: string, el: HTMLElement | null) => void;
}

const activities = [
  {
    icon: <Utensils size={18} className="text-[#e8a030]" />,
    time: '07:00 – 08:00',
    title: 'Ăn sáng – Bún cá Trị An',
    desc: 'Thưởng thức bún cá Trị An – đặc sản địa phương đậm đà hương vị sông nước.',
    accent: '#e8a030',
  },
  {
    icon: <TreePine size={18} className="text-[#4caf7d]" />,
    time: '08:00 – 09:00',
    title: 'Thông điệp tự hào – Cây cam kết xanh',
    desc: 'Sinh viên viết lời nhắn cá nhân và treo lên cây cam kết.',
    accent: '#4caf7d',
  },
  {
    icon: <Fish size={18} className="text-[#a8d87a]" />,
    time: '09:10 – 12:00',
    title: 'Hoàn thiện khu dòng nước – thả cá (Đào mương cá 2)',
    desc: 'Tiếp tục đào – xúc đất, hoàn thiện dòng chảy, chỉnh khu quan sát và cảnh quan.',
    accent: '#a8d87a',
  },
  {
    icon: <Utensils size={18} className="text-[#e8a030]" />,
    time: '12:00 – 13:30',
    title: 'Ăn trưa – nghỉ ngơi',
    desc: 'Bữa trưa và thời gian nghỉ ngơi phục hồi năng lượng.',
    accent: '#e8a030',
  },
  {
    icon: <Hammer size={18} className="text-[#f5c96a]" />,
    time: '13:30 – 17:00',
    title: 'Làm cầu tầm vông (giai đoạn 1 – 2/3)',
    desc: 'Sinh viên tham gia đan tầm vông, lắp khung, cố định và định hình cầu theo hướng dẫn.',
    accent: '#f5c96a',
  },
  {
    icon: <Camera size={18} className="text-[#4caf7d]" />,
    time: '17:00 – 18:00',
    title: 'Ghi nhận thành quả theo đội',
    desc: 'Chụp ảnh, quay flycam khu dòng nước và phần cầu sau ngày thứ 2.',
    accent: '#4caf7d',
  },
  {
    icon: <Utensils size={18} className="text-[#e8a030]" />,
    time: '18:00 – 19:20',
    title: 'Ăn tối – sinh hoạt cá nhân',
    desc: 'Bữa tối và thời gian tự do cá nhân.',
    accent: '#e8a030',
  },
  {
    icon: <Users size={18} className="text-[#a8d87a]" />,
    time: '19:30 – 21:00',
    title: 'Đêm kết nối đội nhóm',
    desc: 'Hoạt động nhẹ, vòng tròn chia sẻ và ghi nhận đóng góp của nhau.',
    accent: '#a8d87a',
  },
];

export default function Slide12Day2({ id, registerRef }: Props) {
  return (
    <SlideWrapper id={id} registerRef={registerRef} className="bg-[#0a1a0f]">
      <div className="absolute inset-0">
        <img
          src="https://images.unsplash.com/photo-1540378261825-7f58f2e17668"
          alt="Bình minh rực rỡ trên mặt hồ yên tĩnh, ánh sáng vàng phản chiếu trên mặt nước trong xanh"
          className="w-full h-full object-cover opacity-10"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#1a3a2a]/60 via-[#0a1a0f] to-[#0a1a0f]" />
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[rgba(76,175,125,0.3)] to-transparent" />
      </div>

      <div className="relative z-10 max-w-screen-xl mx-auto px-6 lg:px-16 xl:px-24 py-24 pt-28 min-h-screen flex flex-col justify-center">
        <div className="fade-in-up mb-4">
          <div className="chapter-badge">
            <span className="w-1.5 h-1.5 rounded-full bg-[#e8a030] inline-block" />
            Hành trình 8 ngày 7 đêm
          </div>
        </div>

        <div className="fade-in-up mb-3">
          <h2 className="font-display font-800 text-[clamp(24px,3.5vw,48px)] leading-[1.1] text-[#f0ede6] tracking-tight">
            NGÀY 2
          </h2>
          <h2 className="font-display font-800 text-[clamp(18px,2.5vw,32px)] leading-[1.3] tracking-tight text-gradient-green">
            Cam kết xanh – Cầu tầm vông – Kết nối đội
          </h2>
        </div>

        <div className="fade-in-up mb-8">
          <div className="section-divider" />
        </div>

        <div className="relative fade-in-up">
          <div className="absolute left-[22px] top-0 bottom-0 w-px bg-gradient-to-b from-[#e8a030]/60 via-[#4caf7d]/40 to-transparent" />
          <div className="flex flex-col gap-4">
            {activities.map((act, i) => (
              <div key={`d2-${i}`} className="flex items-start gap-4 pl-2">
                <div
                  className="flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center z-10"
                  style={{ background: `${act.accent}22`, border: `1.5px solid ${act.accent}55` }}
                >
                  {act.icon}
                </div>
                <div
                  className="flex-1 rounded-xl p-4 transition-all duration-300 hover:-translate-y-0.5"
                  style={{ background: `${act.accent}0d`, border: `1px solid ${act.accent}22` }}
                >
                  <div className="flex flex-wrap items-center gap-2 mb-1">
                    <span
                      className="font-body font-700 text-[11px] tabular-nums px-2 py-0.5 rounded-full"
                      style={{ color: act.accent, background: `${act.accent}18` }}
                    >
                      {act.time}
                    </span>
                    <h3 className="font-display font-700 text-[14px] lg:text-[15px] text-[#f0ede6]">
                      {act.title}
                    </h3>
                  </div>
                  <p className="font-body text-[12px] lg:text-[13px] text-[#a0b8a8] leading-relaxed">
                    {act.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </SlideWrapper>
  );
}
